import { Stack, Typography, useTheme } from '@mui/material';
import {
  PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID,
  PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
} from '@provision/service-level-utils';
import { useCallback, useMemo } from 'react';
import {
  DynamicIcon,
  FormAutocompleteProps,
  FormLabel,
  FormTextFieldProps,
} from 'src/components';
import { SidePanelRow } from 'src/components/SidePanelRow';
import { ServiceLevelSelectMenuField } from 'src/components/SidePanelTabs/service-level-select-menu-field';
import { BooleanSelectMenuField } from 'src/components/SidePanelTabs/tos-accepted-select-menu-field';
import { SAVE_DEBOUNCE_DELAY } from 'src/constants/delays';
import { componentsFontSizes } from 'src/constants/layout';
import {
  Address,
  Conversation,
  formatServiceLevelWithCpp,
  getServiceLevelValue,
  PartnerGroupRules,
  useAddress,
  useGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
  useServiceLevels,
} from 'src/graphql';
import { getCorrectSubscriptionIdFromAddress } from 'src/graphql/entities/address/utils';
import { GetStripeSubscriptionDetailsResponse } from 'src/graphql/entities/stripe-subscription-details';
import { ScheduleCreator } from 'src/graphql/entities/stripe-subscription-schedule';
import { Modals, useShowModals } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { getMembershipInfoChanges, isCPPExpired } from './subscription-details.utils';

export interface MembershipInformationFormValues {
  platformSlaId: { id: string; label: string };
  tosAccepted: { id: string; label: string };
  membershipSalesNotes: string;
}

interface MembershipInfoFormProps {
  address: Partial<Address>;
  conversation?: Conversation;
  disabled?: boolean;
  addCppToSchedule: ScheduleCreator;
  isCppDisabled?: boolean;
  subscriptionDetails?: GetStripeSubscriptionDetailsResponse;
}

const tosAcceptedOptions = [
  { id: 'Yes', label: 'Accepted' },
  { id: 'No', label: 'Not Accepted' },
];

const membershipRegisteredOptions = [
  { id: 'Yes', label: 'Yes' },
  { id: 'No', label: 'No' },
];

export const useMembershipInfoForm = ({
  address,
  conversation,
  disabled,
  addCppToSchedule,
  isCppDisabled,
  subscriptionDetails,
}: MembershipInfoFormProps) => {
  const theme = useTheme();
  const { checkValidity, loading: loadingGroupRules } = useGroupRules();
  const {
    serviceLevelOptions,
    loading: loadingServiceLevels,
    hasServiceLevelAccess,
  } = useServiceLevels(address.partner?.id ?? address.partnerId);
  const { partnerGroupRules, loading: loadingPartnerGroupRules } = usePartnerGroupRules(
    address.partner?.id ?? address.partnerId ?? conversation?.partnerId,
  );
  const isBpoPartner = partnerGroupRules[PartnerGroupRules.AccessToOnevisionBpo];
  const { updateAddress } = useAddress(address.addressId);
  const { showSuccessAlert } = useNotifications();
  const [openAddComplimentaryPeriodModal] = useShowModals(Modals.AddComplimentaryPeriod);

  const onSubmit = useCallback(
    async (values: MembershipInformationFormValues) => {
      const changes = getMembershipInfoChanges(values, address, serviceLevelOptions);
      if (!changes) {
        return;
      }

      await updateAddress(
        {
          ...address,
          ...changes,
        },
        conversation?.conversationId,
        () => {
          if (changes.tosAccepted !== undefined) {
            showSuccessAlert('Membership information Updated');
          }
        },
      );
    },
    [
      address,
      conversation?.conversationId,
      showSuccessAlert,
      updateAddress,
      serviceLevelOptions,
    ],
  );

  const canUpdateSLA = checkValidity([UserGroupRules.CanUpdateSLAs]);

  const currentServiceLevel = useMemo(() => {
    const effectivePlatformSlaId = getServiceLevelValue({
      platformSlaId: address.platformSlaId,
      partner: address.partner ?? conversation?.partner,
      timeZone: address.partner?.timeZone ?? conversation?.partner?.timeZone!,
    });

    if (effectivePlatformSlaId === undefined) {
      return null;
    }

    if (effectivePlatformSlaId === PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID) {
      return {
        id: PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
        label: PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
      };
    }

    const displayName = effectivePlatformSlaId
      ? serviceLevelOptions.find((option) => option.platformSlaId === effectivePlatformSlaId)
          ?.label
      : undefined;
    const label = formatServiceLevelWithCpp(displayName, address.cppExpDate);

    return { id: label, label };
  }, [
    serviceLevelOptions,
    address.platformSlaId,
    address.cppExpDate,
    address.partner,
    conversation?.partner,
  ]);

  const addressSubscriptionId = useMemo(
    () => getCorrectSubscriptionIdFromAddress(address as Address),
    [address],
  );

  const currentTosAccepted = useMemo(() => {
    return tosAcceptedOptions.find((item) => item.id === address?.tosAccepted);
  }, [address?.tosAccepted]);

  const currentMembershipRegistered = useMemo(() => {
    return membershipRegisteredOptions.find((item) => item.id === address?.creditCardProvided);
  }, [address?.creditCardProvided]);

  const formFields = useMemo(() => {
    const commonProps = {
      disabled,
      sx: {
        ...theme.typography.body2,
        fontSize: theme.utils.important(componentsFontSizes.input),
      },
    };

    const fields = [
      ...(hasServiceLevelAccess && isBpoPartner
        ? [
            {
              type: 'autocomplete',
              name: 'platformSlaId' as const,
              label: 'Service Level',
              required: 'Service Level is required',
              options: serviceLevelOptions,
              disabledTooltip: 'Site is on a subscription',
              defaultValue: currentServiceLevel,
              ...commonProps,
              renderField: (
                field: FormAutocompleteProps & { disabledTooltip?: string },
                formDisabled?: boolean,
              ) => (
                <ServiceLevelSelectMenuField
                  field={field}
                  serviceLevelOptions={serviceLevelOptions}
                  cppExpDate={address.cppExpDate}
                  disabled={formDisabled}
                />
              ),
              disabled: disabled || Boolean(!canUpdateSLA || addressSubscriptionId),
              loading: loadingServiceLevels,
            },
          ]
        : []),
      ...(!isBpoPartner
        ? [
            {
              type: 'textfield',
              name: 'membership' as const,
              label: 'Membership',
              defaultValue:
                subscriptionDetails?.partnerProduct?.displayName ?? 'No membership',
              disabled: true,
              icon: (
                <DynamicIcon
                  name="id-card"
                  library="lucide"
                  color={theme.palette.custom.OV.sys.outline}
                />
              ),
              renderField: renderCPPField,
            },
          ]
        : []),
      {
        type: 'autocomplete',
        name: 'tosAccepted' as const,
        label: 'Terms of Service',
        required: 'Terms of Service is required',
        options: tosAcceptedOptions,
        defaultValue: currentTosAccepted,
        ...commonProps,
        renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
          <BooleanSelectMenuField field={field} disabled={formDisabled} />
        ),
      },
      {
        type: 'autocomplete',
        name: 'creditCardProvided' as const,
        label: 'Membership Registered',
        required: 'Terms of Service is required',
        options: membershipRegisteredOptions,
        defaultValue: currentMembershipRegistered,
        ...commonProps,
        renderField: (field: FormAutocompleteProps) => (
          <BooleanSelectMenuField field={field} disabled />
        ),
        disabled: true,
      },
      {
        type: 'textfield',
        name: 'cppExpDate' as const,
        defaultValue: address?.cppExpDate || 'Not Found',
        label: 'CPP Expiration',
        disabled: disabled || !canUpdateSLA || isCppDisabled,

        onClick: () => {
          openAddComplimentaryPeriodModal({
            address: address as Address,
            addCppToSchedule,
          });
        },
        icon: address?.cppExpDate ? (
          <DynamicIcon
            name="calendar"
            library="lucide"
            color={
              isCPPExpired(address?.cppExpDate)
                ? theme.palette.custom.OV.sys.error
                : theme.palette.custom.OV.sys.success
            }
          />
        ) : undefined,
        renderField: renderCPPField,
      },
      {
        type: 'rich-text-bubble',
        label: 'Notes',
        name: 'membershipSalesNotes' as const,
        defaultValue: address?.membershipSalesNotes || '',
        disabled,
        sx: {
          marginTop: theme.spacing(2),
        },
        debounced: SAVE_DEBOUNCE_DELAY,
      },
    ] as FormAutocompleteProps[];

    return fields;
  }, [
    serviceLevelOptions,
    address,
    openAddComplimentaryPeriodModal,
    canUpdateSLA,
    currentMembershipRegistered,
    currentServiceLevel,
    subscriptionDetails?.partnerProduct?.displayName,
    currentTosAccepted,
    addCppToSchedule,
    disabled,
    theme,
    addressSubscriptionId,
    isCppDisabled,
    hasServiceLevelAccess,
    isBpoPartner,
    loadingServiceLevels,
  ]);

  return {
    onSubmit,
    formFields,
    loading: loadingServiceLevels || loadingGroupRules || loadingPartnerGroupRules,
  };
};

export function renderCPPField(field: FormTextFieldProps) {
  const { defaultValue, label, icon, onClick, disabled } = field;

  return (
    <SidePanelRow key={label}>
      <FormLabel label={label} />
      <Stack
        direction="row"
        alignItems="center"
        width="100%"
        gap={1}
        sx={{
          padding: '4px 8px',
          borderRadius: (theme) => theme.shape.borderRadius / 8,
          ...(!disabled && {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.custom.OV.sys.surface,
            },
          }),
        }}
        onClick={disabled ? undefined : onClick}
      >
        <Typography
          component="span"
          sx={{
            fontSize: componentsFontSizes.input,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {icon}
          {defaultValue}
        </Typography>
      </Stack>
    </SidePanelRow>
  );
}
